import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

export default function AdminCities() {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [showEdit, setShowEdit] = useState(false);
    const [editData, setEditData] = useState({
        cityName: "",
        areaInput: "",
        areas: [],
    });

    const [showCreate, setShowCreate] = useState(false);
    const [newCity, setNewCity] = useState({
        cityName: "",
        areaInput: "",
        areas: [],
    });

    const createCity = async () => {
        const cityName = newCity.cityName?.trim();

        if (!cityName) {
            return showErrorToast("City name is required");
        }

        if (cityName.length < 2) {
            return showErrorToast("City name must be at least 2 characters");
        }

        if (!/^[a-zA-Z\s]+$/.test(cityName)) {
            return showErrorToast("City name can contain only letters and spaces");
        }

        const cleanedAreas = [...new Set(
            (newCity.areas || [])
                .map(a => a.trim())
                .filter(a => a.length >= 2)
        )];

        if (cleanedAreas.length > 50) {
            return showErrorToast("Too many areas (max 50 allowed)");
        }


        try {
            const res = await makeRequestAuth("post", "/admin/createCity", {
                cityName: newCity.cityName,
                areas: newCity.areas,
            });

            if (res?.error) {
                return showErrorToast(res.message || "Failed to create city");
            }

            showSuccessToast("City created successfully");
            setShowCreate(false);
            setNewCity({ cityName: "", areaInput: "", areas: [] });
            fetchCities();

        } catch (error) {
            showErrorToast(error.message || "Create failed");
        }
    };

    const addAreaChip = () => {
        const area = newCity.areaInput.trim();
        if (!area) return;

        if (newCity.areas.includes(area)) {
            return showErrorToast("Area already added");
        }

        setNewCity({
            ...newCity,
            areas: [...newCity.areas, area],
            areaInput: "",
        });
    };

    const removeAreaChip = (area) => {
        setNewCity({
            ...newCity,
            areas: newCity.areas.filter(a => a !== area),
        });
    };


    const addEditArea = () => {
        const area = editData.areaInput?.trim();
        if (!area) return;

        if (editData.areas.includes(area)) {
            return showErrorToast("Area already exists");
        }

        setEditData({
            ...editData,
            areas: [...editData.areas, area],
            areaInput: "",
        });
    };

    const removeEditArea = (area) => {
        setEditData({
            ...editData,
            areas: editData.areas.filter(a => a !== area),
        });
    };

    const fetchCities = async () => {
        setLoading(true);
        try {
            const res = await makeRequestAuth("post", "/admin/getCityList", {
                keyword: search,
            });

            if (res?.error) {
                setCities([]);
                return showErrorToast(res.message || "Failed to fetch cities");
            }

            setCities(res.data || []);
        } catch (error) {
            showErrorToast("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCities();
    }, [search]);

    const updateCity = async () => {
        const cityName = editData.cityName?.trim();

        if (!cityName) {
            return showErrorToast("City name is required");
        }

        if (cityName.length < 2) {
            return showErrorToast("City name must be at least 2 characters");
        }

        if (!/^[a-zA-Z\s]+$/.test(cityName)) {
            return showErrorToast("City name can contain only letters and spaces");
        }

        try {
            const res = await makeRequestAuth("post", "/admin/updateCity", {
                cityId: editData._id,
                cityName,
                areas: editData.areas,
            });

            if (res?.error) {
                return showErrorToast(res.message || "Update failed");
            }

            showSuccessToast("City updated successfully");
            setShowEdit(false);
            fetchCities();

        } catch (error) {
            showErrorToast("Update failed");
        }
    };


    return (
        <div className="p-6">

            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
                <ToastContainer />
                <h2 className="text-2xl font-semibold text-[#434646]">
                    City Management
                </h2>

                <button
                    onClick={() => setShowCreate(true)}
                    className="px-4 py-2 rounded-xl bg-[#24ADD7] text-white"
                >
                    + Add City
                </button>

            </div>

            {/* SEARCH */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search city..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border px-4 py-2 rounded-xl w-[220px]"
                />
            </div>

            {/* LOADING */}
            {loading && (
                <div className="text-center py-20 text-gray-500">
                    Loading cities...
                </div>
            )}

            {/* NO DATA */}
            {!loading && cities.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                    No cities found
                </div>
            )}

            {/* CITY CARDS */}
            {!loading && cities.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {cities.map((city) => (
                        <div
                            key={city._id}
                            className="bg-white rounded-2xl shadow p-4"
                        >
                            <h3 className="text-lg font-semibold mb-2">
                                {city.cityName}
                            </h3>

                            <p className="text-sm text-gray-500 mb-3">
                                Areas: {city.areas?.length || 0}
                            </p>

                            <div className="flex justify-between items-center">
                                <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-600">
                                    Active
                                </span>

                                <button
                                    onClick={() => {
                                        setEditData(city);
                                        setShowEdit(true);
                                    }}
                                    className="px-3 py-1 border rounded-lg text-sm"
                                >
                                    Edit
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6">

                        <h3 className="text-xl font-semibold mb-4">
                            Create City
                        </h3>

                        {/* CITY NAME */}
                        <input
                            type="text"
                            placeholder="City name"
                            value={newCity.cityName}
                            onChange={(e) =>
                                setNewCity({ ...newCity, cityName: e.target.value })
                            }
                            className="w-full border px-4 py-2 rounded-xl mb-4"
                        />

                        {/* AREA INPUT */}
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                placeholder="Enter area"
                                value={newCity.areaInput}
                                onChange={(e) =>
                                    setNewCity({ ...newCity, areaInput: e.target.value })
                                }
                                className="flex-1 border px-4 py-2 rounded-xl"
                            />

                            <button
                                onClick={addAreaChip}
                                className="px-4 py-2 bg-[#24ADD7] text-white rounded-xl"
                            >
                                Add
                            </button>
                        </div>

                        {/* AREA CHIPS */}
                        <div className="flex flex-wrap gap-2 mb-6">
                            {newCity.areas.map((area, i) => (
                                <span
                                    key={i}
                                    className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                                >
                                    {area}
                                    <button
                                        onClick={() => removeAreaChip(area)}
                                        className="text-blue-700 font-bold"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>

                        {/* ACTIONS */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowCreate(false)
                                    setNewCity({ cityName: "", areas: [] })
                                }}
                                className="px-4 py-2 border rounded-xl"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={createCity}
                                className="px-4 py-2 bg-[#24ADD7] text-white rounded-xl"
                            >
                                Create
                            </button>
                        </div>

                    </div>
                </div>
            )}


            {/* EDIT MODAL */}
            {showEdit && editData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6">

                        <h3 className="text-xl font-semibold mb-4">
                            Edit City
                        </h3>

                        {/* CITY NAME */}
                        <input
                            type="text"
                            value={editData.cityName}
                            onChange={(e) =>
                                setEditData({ ...editData, cityName: e.target.value })
                            }
                            className="w-full border px-4 py-2 rounded-xl mb-4"
                        />

                        {/* ADD AREA */}
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                placeholder="Enter area"
                                value={editData.areaInput || ""}
                                onChange={(e) =>
                                    setEditData({ ...editData, areaInput: e.target.value })
                                }
                                className="flex-1 border px-4 py-2 rounded-xl"
                            />

                            <button
                                onClick={addEditArea}
                                className="px-4 py-2 bg-[#24ADD7] text-white rounded-xl"
                            >
                                Add
                            </button>
                        </div>

                        {/* AREA CHIPS */}
                        <div className="flex flex-wrap gap-2 mb-6">
                            {editData.areas?.map((area, i) => (
                                <span
                                    key={i}
                                    className="flex items-center gap-2 bg-orange-100 text-[#24ADD7] px-3 py-1 rounded-full text-sm"
                                >
                                    {area}
                                    <button
                                        onClick={() => removeEditArea(area)}
                                        className="font-bold"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>

                        {/* ACTIONS */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowEdit(false)}
                                className="px-4 py-2 border rounded-xl"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={updateCity}
                                className="px-4 py-2 bg-[#24ADD7] text-white rounded-xl"
                            >
                                Update
                            </button>
                        </div>

                    </div>
                </div>
            )}


        </div>
    );
}
